/**
 * Bulk Reassignment Modal
 * ----------------------------------------------------------------
 * Admin-only: move every pending / in-progress compliance occurrence
 * from one team member to another in a single action. Skips items
 * already marked Completed. Used when a team member leaves or
 * changes portfolios.
 */
import React, { useEffect, useState } from 'react';
import { X, ArrowRight, AlertTriangle, Loader, History as HistoryIcon } from 'lucide-react';
import { complianceAPI } from '../../services/api';
import { useDateFormat } from '../../hooks/useDateFormat';

const BulkReassignModal = ({ onClose, onDone }) => {
  const fmtDate = useDateFormat();
  const [team, setTeam] = useState([]);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingCount, setPendingCount] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [tRes, hRes] = await Promise.all([
          complianceAPI.listTeamMembers(),
          complianceAPI.bulkTransferHistory(),
        ]);
        setTeam(tRes.data || []);
        setHistory(hRes.data?.history || []);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load team members');
      }
    })();
  }, []);

  // Preview how many occurrences will be moved so the admin knows the
  // blast radius before confirming.
  useEffect(() => {
    if (!fromId) { setPendingCount(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await complianceAPI.listOccurrences({
          assignee_id: fromId, scope: 'all',
        });
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : (res.data?.rows || []);
        setPendingCount(rows.filter(o => (o.wip_status || '') !== 'Completed').length);
      } catch {
        if (!cancelled) setPendingCount(null);
      }
    })();
    return () => { cancelled = true; };
  }, [fromId]);

  const fromUser = team.find(t => t.id === fromId);
  const toUser = team.find(t => t.id === toId);
  const canSubmit = fromId && toId && fromId !== toId && !saving;

  const handleTransfer = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const res = await complianceAPI.bulkTransferAssignee({
        from_user_id: fromId, to_user_id: toId, reason: reason || null,
      });
      onDone?.(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         data-testid="bulk-reassign-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">Bulk Reassign Compliance</h3>
            <p className="text-xs text-orange-100">Transfer every pending item from one member to another</p>
          </div>
          <button onClick={onClose} className="hover:opacity-70" data-testid="bulk-reassign-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg px-4 py-2">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From member</label>
              <select value={fromId} onChange={e => setFromId(e.target.value)}
                      className="border rounded-lg px-2 py-2 text-sm w-full"
                      data-testid="bulk-reassign-from">
                <option value="">Select…</option>
                {team.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <ArrowRight size={20} className="text-gray-400 mb-2 hidden md:block" />
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To member</label>
              <select value={toId} onChange={e => setToId(e.target.value)}
                      className="border rounded-lg px-2 py-2 text-sm w-full"
                      data-testid="bulk-reassign-to">
                <option value="">Select…</option>
                {team.filter(t => t.id !== fromId).map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Reason (optional, saved to audit trail)
            </label>
            <input type="text" value={reason} onChange={e => setReason(e.target.value)}
                   placeholder="e.g. Ravi is leaving the team on 30-Sep-2026"
                   className="border rounded-lg px-3 py-2 text-sm w-full"
                   data-testid="bulk-reassign-reason" />
          </div>

          {pendingCount !== null && fromUser && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3 text-sm flex items-start gap-2"
                 data-testid="bulk-reassign-preview">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                <b>{pendingCount}</b> pending / in-progress compliance
                occurrence{pendingCount === 1 ? '' : 's'} will move from
                &nbsp;<b>{fromUser.name}</b>&nbsp;
                {toUser && <>to <b>{toUser.name}</b>.</>}
                <br />
                Already-Completed items stay with {fromUser.name} for
                historical accuracy. Any pending reassignment requests
                addressed to {fromUser.name} are auto-declined.
              </div>
            </div>
          )}

          {history.length > 0 && (
            <div className="border rounded-lg">
              <button onClick={() => setShowHistory(v => !v)}
                      className="w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      data-testid="bulk-reassign-history-toggle">
                <span className="inline-flex items-center gap-1.5">
                  <HistoryIcon size={14} /> Recent bulk transfers ({history.length})
                </span>
                <span className="text-xs text-gray-400">{showHistory ? 'Hide' : 'Show'}</span>
              </button>
              {showHistory && (
                <div className="border-t px-4 py-2 text-xs max-h-40 overflow-y-auto">
                  {history.map(h => (
                    <div key={h.id} className="py-1 border-b last:border-0" data-testid={`bulk-history-${h.id}`}>
                      <b>{h.from_user_name}</b> → <b>{h.to_user_name}</b>
                      &nbsp;· {h.transferred_count} moved
                      &nbsp;· <span className="text-gray-500">{fmtDate(h.created_at)}</span>
                      {h.reason && <div className="text-gray-600 italic">"{h.reason}"</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
          <button onClick={onClose}
                  className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-100"
                  data-testid="bulk-reassign-cancel">
            Cancel
          </button>
          {!confirming ? (
            <button onClick={() => setConfirming(true)}
                    disabled={!canSubmit}
                    className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                    data-testid="bulk-reassign-review">
              Review
            </button>
          ) : (
            <button onClick={handleTransfer}
                    disabled={!canSubmit}
                    className="px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid="bulk-reassign-confirm">
              {saving && <Loader size={13} className="animate-spin" />}
              Confirm transfer{pendingCount ? ` (${pendingCount})` : ''}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkReassignModal;
